import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import FilterPopover from './FilterPopover.jsx';
import GroupEmptyState from './GroupEmptyState.jsx';
import GroupResidentsCell from './GroupResidentsCell.jsx';

/**
 * Column definitions for the groups table.
 */
export const GROUP_COLUMN_DEFS = [
  { key: 'resident_group_name', label: 'Group Name', sortable: true, filterable: true, required: true },
  { key: 'resident_group_category_name', label: 'Category', sortable: true, filterable: true, required: false },
  { key: 'resident_description', label: 'Description', sortable: true, filterable: false, required: false },
  { key: 'resident_count', label: 'Residents', sortable: true, filterable: false, required: true },
];

/**
 * GroupsTable — data table for resident groups with sorting, filtering,
 * sticky header, row selection, and row actions.
 */
const GroupsTable = ({
  data,
  allData,
  visibleColumns,
  sortColumn,
  sortOrder,
  onSort,
  filters,
  onFilterChange,
  selectedIds,
  onSelectRow,
  onSelectAll,
  onEditGroup,
  onDeleteGroup,
  onAddGroup,
  onClearFilters,
}) => {
  const activeColumns = useMemo(
    () => GROUP_COLUMN_DEFS.filter((col) => visibleColumns.includes(col.key)),
    [visibleColumns]
  );

  /** Collect unique filter values from the full (unfiltered) dataset */
  const getFilterValues = useCallback(
    (columnKey) => {
      const values = new Set();
      (allData || data).forEach((row) => {
        const val = row[columnKey];
        values.add(val != null && val !== '' ? String(val) : '(empty)');
      });
      return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    },
    [allData, data]
  );

  const allSelected = data.length > 0 && data.every((r) => selectedIds.includes(r.resident_group_id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const renderSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return (
        <svg className="rp-icon-sm rp-sort-icon rp-sort-icon--neutral" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg className="rp-icon-sm rp-sort-icon rp-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
      </svg>
    ) : (
      <svg className="rp-icon-sm rp-sort-icon rp-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
      </svg>
    );
  };

  /** Renders a single cell value */
  const renderCell = (row, columnKey) => {
    switch (columnKey) {
      case 'resident_group_name':
        return (
          <span className="rp-cell-text" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
            {row.resident_group_name}
          </span>
        );

      case 'resident_group_category_name':
        return row.resident_group_category_name ? (
          <span className="rp-area-type-tag" title={row.resident_group_category_name}>
            {row.resident_group_category_name}
          </span>
        ) : (
          <span className="rp-cell-text--muted">—</span>
        );

      case 'resident_description':
        return row.resident_description ? (
          <span className="rp-cell-text rp-cell-text--truncate" title={row.resident_description}>
            {row.resident_description}
          </span>
        ) : (
          <span className="rp-cell-text--muted">—</span>
        );

      case 'resident_count':
        return <GroupResidentsCell residents={row.members || []} />;

      default:
        return <span className="rp-cell-text">{row[columnKey] || '—'}</span>;
    }
  };

  if (data.length === 0) {
    const hasAnyData = (allData || []).length > 0;
    return (
      <GroupEmptyState
        type={hasAnyData ? 'no-results' : 'no-data'}
        onAddGroup={hasAnyData ? undefined : onAddGroup}
        onClearFilters={hasAnyData ? onClearFilters : undefined}
      />
    );
  }

  return (
    <div className="rp-table-wrap">
      <table className="rp-table">
        <thead className="rp-thead">
          <tr>
            {/* Checkbox column */}
            <th scope="col" className="rp-th rp-th--checkbox">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = someSelected; }}
                onChange={onSelectAll}
                className="rp-checkbox"
                aria-label="Select all groups"
              />
            </th>

            {/* Data columns */}
            {activeColumns.map((col) => (
              <th key={col.key} scope="col" className="rp-th">
                <div className="rp-th__content">
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key)}
                      className="rp-th__sort-btn"
                      aria-label={`Sort by ${col.label}`}
                    >
                      {col.label}
                      {renderSortIcon(col.key)}
                    </button>
                  ) : (
                    <span>{col.label}</span>
                  )}

                  {col.filterable && (
                    <FilterPopover
                      columnKey={col.key}
                      label={col.label}
                      values={getFilterValues(col.key)}
                      selectedValues={filters[col.key] || []}
                      onChange={onFilterChange}
                    />
                  )}
                </div>
              </th>
            ))}

            {/* Actions column */}
            <th scope="col" className="rp-th rp-th--actions">
              <span className="rp-sr-only">Actions</span>
            </th>
          </tr>
        </thead>

        <tbody className="rp-tbody">
          {data.map((row) => {
            const isSelected = selectedIds.includes(row.resident_group_id);
            return (
              <tr
                key={row.resident_group_id}
                className={`rp-tr ${isSelected ? 'is-selected' : ''}`}
              >
                {/* Checkbox */}
                <td className="rp-td rp-td--checkbox">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onSelectRow(row.resident_group_id)}
                    className="rp-checkbox"
                    aria-label={`Select ${row.resident_group_name}`}
                  />
                </td>

                {/* Data cells */}
                {activeColumns.map((col) => (
                  <td key={col.key} className="rp-td">
                    {renderCell(row, col.key)}
                  </td>
                ))}

                {/* Row actions kebab */}
                <td className="rp-td rp-td--actions">
                  <GroupRowActions
                    group={row}
                    onEdit={onEditGroup}
                    onDelete={onDeleteGroup}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * GroupRowActions — kebab menu for per-row actions.
 */
const GroupRowActions = ({ group, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="rp-row-actions" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="rp-row-actions__trigger"
        aria-label={`Actions for ${group.resident_group_name}`}
        aria-expanded={open}
      >
        <i className="fe fe-more-vertical" aria-hidden="true" />
      </button>

      {open && (
        <div className="rp-row-actions__menu">
          <button
            type="button"
            onClick={() => { onEdit(group); setOpen(false); }}
            className="rp-row-actions__item"
          >
            <svg className="rp-icon-md rp-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
            </svg>
            Edit Group
          </button>
          <button
            type="button"
            onClick={() => { onDelete(group); setOpen(false); }}
            className="rp-row-actions__item rp-row-actions__item--danger"
          >
            <svg className="rp-icon-md rp-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
            Delete Group
          </button>
        </div>
      )}
    </div>
  );
};

export default GroupsTable;
